import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    openRecentSearchesPanel,
    selectIsFullPanelOpen,
    selectSearchInputFocused,
    selectSearchQuery,
    setSearchInputFocused,
    setSearchQuery,
} from "../slice"
import { selectRecentSearchResults } from "./selectors"
import { SearchResultRow } from "./SearchResultRow"
import { recordRecentSearch } from "./thunks"
import { useSearchResults } from "./useSearchResults"
import classNames from "classnames"
import { RECENT_SEARCHES_PREVIEW_COUNT } from "@nova/constants"

export const OverviewSearchDropdown = () => {
    const dispatch = useAppDispatch()
    const inputFocused = useAppSelector(selectSearchInputFocused)
    const isFullPanelOpen = useAppSelector(selectIsFullPanelOpen)
    const query = useAppSelector(selectSearchQuery)
    const recentSearches = useAppSelector(selectRecentSearchResults)

    if (!inputFocused || isFullPanelOpen) return null
    if (!query && !recentSearches.length) return null

    const afterSelect = () => {
        dispatch(setSearchInputFocused(false))
        dispatch(setSearchQuery(""))
    }

    return (
        <div className="absolute top-full left-0 mt-2 w-full bg-black-800 backdrop-blur-sm border border-white-100 rounded-2xl overflow-hidden pointer-events-auto p-2">
            {query ? (
                <SearchResults query={query} onAfterSelect={afterSelect} />
            ) : (
                <RecentSearchesPreview onAfterSelect={afterSelect} />
            )}
        </div>
    )
}

const RecentSearchesPreview = ({ onAfterSelect }: { onAfterSelect: () => void }) => {
    const dispatch = useAppDispatch()
    const allResults = useAppSelector(selectRecentSearchResults)
    const resultsPreview = allResults.slice(0, RECENT_SEARCHES_PREVIEW_COUNT)
    const canOpenRecentsPanel = allResults.length > RECENT_SEARCHES_PREVIEW_COUNT

    return (
        <div className="flex flex-col">
            <div
                className={classNames("p-2 flex gap-1 items-center group", {
                    "cursor-pointer": canOpenRecentsPanel,
                })}
                onClick={() => {
                    if (!canOpenRecentsPanel) return
                    dispatch(openRecentSearchesPanel())
                }}
            >
                <div
                    className={classNames("telegraf text-lg text-gray-100", {
                        "group-hover:text-white": canOpenRecentsPanel,
                    })}
                >
                    Recently Searched
                </div>
                {canOpenRecentsPanel && (
                    <Icons.SmallIcon
                        Icon={Icons.ChevronRight}
                        className="text-gray-500 group-hover:text-gray-400 group-hover:translate-x-1 transition-transform"
                    />
                )}
            </div>
            {resultsPreview.map((entry) => (
                <SearchResultRow
                    key={entry.plugin.getKey(entry.data)}
                    entry={entry}
                    onAfterSelect={() => {
                        dispatch(
                            recordRecentSearch({
                                pluginName: entry.plugin.name,
                                data: entry.data,
                            })
                        )
                        onAfterSelect()
                    }}
                />
            ))}
        </div>
    )
}

const SearchResults = ({ query, onAfterSelect }: { query: string; onAfterSelect: () => void }) => {
    const dispatch = useAppDispatch()
    const { results, isLoading } = useSearchResults(query)

    if (isLoading) return <div className="px-3 py-2 text-gray-500">Loading...</div>

    return (
        <ScrollableArea className="max-h-[400px]" onWheel={(e) => e.stopPropagation()}>
            <div className="px-2.5 py-2 text-lg text-gray-100 telegraf">Results</div>
            <div className="flex flex-col">
                {results.map((entry) => (
                    <SearchResultRow
                        key={entry.plugin.getKey(entry.data)}
                        entry={entry}
                        onAfterSelect={() => {
                            dispatch(
                                recordRecentSearch({
                                    pluginName: entry.plugin.name,
                                    data: entry.data,
                                })
                            )
                            onAfterSelect()
                        }}
                    />
                ))}
            </div>
        </ScrollableArea>
    )
}
